import { https } from "../http";
import store from "../store";
import { setUploadFileLoading } from "../store/reducers/document";

// Get Pre-Signed URL for Upload
export const getUploadUrlApi = async ({ filename, type }) => {
  try {
    store.dispatch(setUploadFileLoading(true));
    const res = await https().get("/me/generate-presigned-url", {
      params: {
        path: encodeURIComponent(filename), // Encode here
        contentType: type,
      },
    });
    return res?.data;
  } catch (error) {
    store.dispatch(setUploadFileLoading(false));
    console.error("Error fetching pre-signed URL", error);
    throw error;
  } finally {
    store.dispatch(setUploadFileLoading(false));
  }
};

export const putFileToS3Api = async ({ uploadURL, file }) => {
  try {
    // Create URL object to manipulate
    const urlObj = new URL(uploadURL);

    // Extract JUST the base URL without query parameters
    const cleanUrl = `${urlObj.origin}${urlObj.pathname}`;

    const res = await fetch(cleanUrl, {
      // Use clean URL here
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        // Add this header for larger files
        "x-amz-content-sha256": "UNSIGNED-PAYLOAD",
      },
      body: file,
    });

    return res;
  } catch (error) {
    console.error("Error uploading file to S3", error);
    throw error;
  }
};

/**
 * Builds a collision-proof filename so no two uploads can share an S3 object.
 *
 * Every upload lands in one flat `profile_photos/` prefix (see the backend's
 * awsS3GeneratePresignedUrl), so passing the raw filename means two uploads named
 * "photo.jpg" — or two staff licences both named "license.pdf" — write to the
 * *same* object, and every record pointing at that URL silently flips to whichever
 * file was uploaded last. Each upload therefore gets its own token.
 *
 * The token is prepended (never a "/" — a nested key would get double-encoded on
 * the way to the presign endpoint) so the extension is preserved for the viewers
 * that sniff file type from the URL. Use `fileNameFromUrl` in utils to strip the
 * token back off when showing a key to the user.
 */
const buildUniqueFilename = (name, keyPrefix) => {
  const safeName = (name || "file")
    // Spaces/unicode survive the presign round-trip badly; keep the key ASCII-safe.
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    // Keep keys short while holding on to the tail (and therefore the extension).
    .slice(-60);
  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return [keyPrefix, token, safeName].filter(Boolean).join("-");
};

// Master Upload Function
// `unique` defaults to true: overwriting someone else's object is never what a
// caller wants. Pass `unique: false` only for a key that must stay stable.
export const uploadFileToS3Api = async ({ file, unique = true, keyPrefix = "" }) => {
  try {
    if (!file) throw new Error("No file provided");

    const { uploadURL, path } = await getUploadUrlApi({
      filename: unique ? buildUniqueFilename(file.name, keyPrefix) : file.name,
      type: file.type,
    });

    // Create URL object to manipulate
    const urlObj = new URL(uploadURL);

    // Extract JUST the base URL without query parameters
    const cleanUrl = `${urlObj.origin}${urlObj.pathname}`;
    const uploadRes = await putFileToS3Api({ uploadURL, file });
    if (!uploadRes.ok) throw new Error("Failed to upload file to S3");
    return {
      message: "File uploaded successfully",
      uploadURL: cleanUrl, // full PUT url (used only once)
      path, // this should be the actual public S3 path if returned
    };
  } catch (error) {
    console.error("uploadFileToS3Api Error:", error);
    throw error;
  }
};
